import { useEffect, useRef, useState } from 'react'

/**
 * Minimaler Shim für die (nicht standardisierte) Web-Speech-API. Das ist eine Browser-Schnittstelle, kein
 * Datenvertrag — daher hier lokal typisiert (nur die paar Felder, die wir nutzen), statt aus @wf/shared.
 */
interface SpeechRecognitionResultLike {
  0: { transcript: string }
}
interface SpeechRecognitionEventLike {
  results: ArrayLike<SpeechRecognitionResultLike>
}
interface SpeechRecognitionLike {
  lang: string
  interimResults: boolean
  continuous: boolean
  onresult: ((event: SpeechRecognitionEventLike) => void) | null
  onend: (() => void) | null
  onerror: (() => void) | null
  start: () => void
  stop: () => void
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike

function getRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null
  const w = window as unknown as { SpeechRecognition?: SpeechRecognitionCtor; webkitSpeechRecognition?: SpeechRecognitionCtor }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

export interface Dictation {
  /** Spracherkennung im Browser verfügbar? Sonst keinen Diktat-Knopf zeigen. */
  supported: boolean
  listening: boolean
  toggle: () => void
}

/**
 * Sprachdiktat (de-DE) für ein Eingabefeld. `onText` bekommt das erkannte Stück Text, der Aufrufer hängt es
 * an. Nur aktiv, wenn der Browser die Web-Speech-API kann (Feature-Detection) — sonst bleibt der Knopf weg.
 */
export function useDictation(onText: (text: string) => void): Dictation {
  const ctor = getRecognitionCtor()
  const supported = ctor != null
  const [listening, setListening] = useState(false)
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null)
  // onText kann sich pro Render ändern; über ref halten, damit der Recognition-Callback stabil bleibt.
  const onTextRef = useRef(onText)
  onTextRef.current = onText

  useEffect(() => {
    return () => recognitionRef.current?.stop()
  }, [])

  function toggle(): void {
    if (!ctor) return
    if (listening) {
      recognitionRef.current?.stop()
      return
    }
    const recognition = new ctor()
    recognition.lang = 'de-DE'
    recognition.interimResults = false
    recognition.continuous = false
    recognition.onresult = (event) => {
      const transcript = event.results[0]?.[0]?.transcript?.trim()
      if (transcript) onTextRef.current(transcript)
    }
    recognition.onend = () => {
      setListening(false)
      recognitionRef.current = null
    }
    recognition.onerror = () => {
      setListening(false)
      recognitionRef.current = null
    }
    recognitionRef.current = recognition
    setListening(true)
    recognition.start()
  }

  return { supported, listening, toggle }
}
